import {
  apiClient,
  isApiError,
  type OperatorAuthorityProfile,
  type OperatorCurrentSession,
  type OperatorPermissions,
  type OperatorUserProfile,
} from "@/lib/api/client"

export interface OperatorUserPermissions extends OperatorPermissions {
  manageAuthorities: boolean
  manageJurisdictions: boolean
}

export type OperatorResolvedSession =
  | {
      actorType: "authority"
      displayName: string
      isPortalAdmin: boolean
      organizationId?: string | null
      organizationName?: string | null
      permissions: OperatorPermissions
      profile: OperatorAuthorityProfile
      roleToken: string
    }
  | {
      actorType: "user"
      displayName: string
      isPortalAdmin: boolean
      organizationId?: string | null
      organizationName?: string | null
      permissions: OperatorUserPermissions
      profile: OperatorUserProfile
      roleToken: string
    }

interface ResolveOperatorSessionOptions {
  allowAuthority?: boolean
  allowProductUser?: boolean
  requiredAccess?: (session: OperatorResolvedSession) => boolean
}

const FLEET_OPERATOR_ROLE_TOKENS = new Set([
  "fleet_owner",
  "fleet_admin",
  "fleet_super_admin",
])

const FLEET_ORGANIZATION_TYPES = new Set([
  "fleet_partner",
  "fleet_operator",
  "fleet_safety",
])

function normalizeRoleToken(value: string | null | undefined) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
}

export function isFleetSuperAdminRoleToken(value: string | null | undefined) {
  return normalizeRoleToken(value) === "fleet_super_admin"
}

function normalizeOrganizationType(value: string | null | undefined) {
  return normalizeRoleToken(value)
}

function isFleetRoleToken(value: string | null | undefined) {
  return FLEET_OPERATOR_ROLE_TOKENS.has(normalizeRoleToken(value))
}

function deriveUserPermissions(
  profile: OperatorUserProfile
): OperatorUserPermissions {
  const roleToken = normalizeRoleToken(profile.role)
  const isPortalAdmin = roleToken === "admin" && !profile.organization?.id
  const canManageAuthorities = roleToken === "admin" || roleToken === "manager"

  return {
    authorityRole: isPortalAdmin ? "portal_admin" : roleToken || "viewer",
    dispatchIncidents: canManageAuthorities,
    exportEvidence: roleToken !== "viewer",
    manageAuthorities: canManageAuthorities,
    manageExports: roleToken !== "viewer",
    manageJurisdictions: canManageAuthorities,
    updateAssignments: canManageAuthorities,
    updateIncidentLifecycle: canManageAuthorities,
    viewAgencyIncidents: true,
    viewAllOrganizations: isPortalAdmin,
    viewAnalytics: true,
  }
}

function resolveUserSession(session: OperatorCurrentSession): OperatorResolvedSession {
  const roleToken = normalizeRoleToken(session.user.role)
  const permissions = {
    ...deriveUserPermissions(session.user),
    ...(session.permissions || {}),
  }

  return {
    actorType: "user",
    displayName:
      session.user.name || session.user.email || "Product user",
    isPortalAdmin: Boolean(permissions.viewAllOrganizations),
    organizationId: session.user.organization?.id,
    organizationName:
      session.organization?.name || session.user.organization?.name || null,
    permissions,
    profile: {
      ...session.user,
      organization: session.organization || session.user.organization || null,
    },
    roleToken,
  }
}

export function isOrchardOperatorSession(session: OperatorResolvedSession) {
  if (session.actorType !== "user") {
    return false
  }

  const organizationType = normalizeOrganizationType(
    session.profile.organization?.type
  )

  return organizationType === "orchard_operator"
}

export function isFleetOperatorSession(session: OperatorResolvedSession) {
  if (session.actorType !== "user") {
    return false
  }

  if (isFleetRoleToken(session.roleToken)) {
    return true
  }

  const organization = session.profile.organization
  const organizationType = normalizeOrganizationType(organization?.type)

  return (
    FLEET_ORGANIZATION_TYPES.has(organizationType)
    || Boolean(organization?.fleetEnabled)
  )
}

export function canAccessFleetSuperAdminControls(session: OperatorResolvedSession) {
  return session.actorType === "user" && isFleetSuperAdminRoleToken(session.roleToken)
}

async function tryResolveProductUserSession() {
  try {
    const currentSession = await apiClient.getCurrentSession()

    return resolveUserSession(currentSession.data)
  } catch (error) {
    void error
    return null
  }
}

async function tryResolveAuthoritySession() {
  try {
    const authorityProfile = await apiClient.getOperatorAuthorityProfile()
    const roleToken = normalizeRoleToken(
      authorityProfile.data.role
        || authorityProfile.data.authorityRole
        || authorityProfile.data.permissions?.authorityRole
    )

    return {
      actorType: "authority",
      displayName:
        authorityProfile.data.fullName
          || authorityProfile.data.email
          || "Authority operator",
      isPortalAdmin: Boolean(authorityProfile.data.isPortalAdmin),
      organizationId: authorityProfile.data.organizationId,
      organizationName:
        authorityProfile.data.organizationName
          || authorityProfile.data.organization?.name
          || null,
      permissions: authorityProfile.data.permissions || {},
      profile: authorityProfile.data,
      roleToken,
    } satisfies OperatorResolvedSession
  } catch (error) {
    if (!isApiError(error) || (error.status !== 401 && error.status !== 403)) {
      throw error
    }

    return null
  }
}

export async function resolveOperatorSession(
  options: ResolveOperatorSessionOptions = {}
): Promise<OperatorResolvedSession> {
  const {
    allowAuthority = true,
    allowProductUser = true,
    requiredAccess,
  } = options
  let fallbackSession: OperatorResolvedSession | null = null

  if (allowProductUser) {
    const productUserSession = await tryResolveProductUserSession()

    if (productUserSession) {
      if (!requiredAccess || requiredAccess(productUserSession)) {
        return productUserSession
      }

      fallbackSession = productUserSession
    }
  }

  if (allowAuthority) {
    const authoritySession = await tryResolveAuthoritySession()

    if (authoritySession) {
      if (!requiredAccess || requiredAccess(authoritySession)) {
        return authoritySession
      }

      fallbackSession ||= authoritySession
    }
  }

  if (fallbackSession) {
    return fallbackSession
  }

  if (!allowProductUser) {
    throw new Error("Authority session required")
  }

  const userProfile = await apiClient.getOperatorUserProfile()

  return resolveUserSession({
    actorType: "user",
    authenticated: true,
    organization: userProfile.data.organization || null,
    permissions: deriveUserPermissions(userProfile.data),
    postAuthDestination: "/account",
    user: userProfile.data,
  })
}

export function canAccessAdminWorkspace(session: OperatorResolvedSession) {
  if (isOrchardOperatorSession(session) || isFleetOperatorSession(session)) {
    return false
  }

  if (session.actorType === "authority") {
    return session.isPortalAdmin || session.roleToken === "org_admin"
  }

  return session.roleToken === "admin" || session.roleToken === "manager"
}

export function canAccessAuthorityWorkspace(session: OperatorResolvedSession) {
  if (isOrchardOperatorSession(session) || isFleetOperatorSession(session)) {
    return false
  }

  return session.actorType === "authority" || canManagePortalOrganizations(session)
}

export function canManagePortalOrganizations(session: OperatorResolvedSession) {
  if (isFleetOperatorSession(session)) {
    return false
  }

  return Boolean(session.isPortalAdmin || session.permissions.viewAllOrganizations)
}

export function getOperatorSessionOrganization(session: OperatorResolvedSession) {
  const profileOrganization = session.profile.organization ?? null

  return {
    id: session.organizationId ?? profileOrganization?.id ?? null,
    name: session.organizationName ?? profileOrganization?.name ?? null,
  }
}

export function canAccessStockOpsWorkspace(session: OperatorResolvedSession) {
  return canAccessAdminWorkspace(session)
}

export function canAccessOrchardWorkspace(session: OperatorResolvedSession) {
  if (isFleetOperatorSession(session)) {
    return false
  }

  if (isOrchardOperatorSession(session)) {
    return true
  }

  if (session.actorType === "authority") {
    return Boolean(
      session.isPortalAdmin
        || session.roleToken === "org_admin"
        || session.permissions.viewAllOrganizations
    )
  }

  return session.roleToken === "admin" || session.roleToken === "manager"
}

export function canAccessOpsMonitoringWorkspace(session: OperatorResolvedSession) {
  if (isOrchardOperatorSession(session) || isFleetOperatorSession(session)) {
    return false
  }

  return Boolean(
    session.permissions.manageAuthorities
      || session.permissions.manageJurisdictions
      || session.permissions.viewAllOrganizations
      || canAccessAdminWorkspace(session)
  )
}

export function getOperatorSessionScopeLabel(session: OperatorResolvedSession) {
  if (session.isPortalAdmin) {
    return "Portal scope"
  }

  if (session.organizationName) {
    return session.organizationName
  }

  return session.organizationId ? `Organization ${session.organizationId}` : "Scoped session"
}

export function titleizeToken(value: string | null | undefined) {
  const normalized = normalizeRoleToken(value)

  if (!normalized) {
    return "Unknown"
  }

  return normalized
    .split("_")
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ")
}
